<div class="min-h-screen">
  <!-- Header -->
  <div class="">
    <div class="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 sm:py-6 gap-4">
        <div>
          <h1 class="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">ROI Management Dashboard</h1>
          <p class="text-sm sm:text-base text-gray-600 mt-1">Monitor and manage Return on Investment calculations and distributions</p>
        </div>
        <div class="flex flex-row sm:flex-row gap-2 sm:gap-3">
          <p-button
            label="Trigger ROI Job"
            icon="pi pi-play"
            [loading]="loading.triggering"
            (onClick)="triggerRoiJob()"
            styleClass="p-button-primary w-full sm:w-auto">
          </p-button>
          <p-button
            label="Refresh Data"
            icon="pi pi-refresh"
            (onClick)="loadAllData()"
            styleClass="p-button-secondary w-full sm:w-auto">
          </p-button>
        </div>
      </div>
    </div>
  </div>

  <!-- Main Content -->
  <div class="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
    <div class="space-y-6 sm:space-y-8 lg:space-y-10">

      <!-- Summary Cards -->
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6" hidden>
        <!-- Total ROI Runs -->
        <div class="bg-white rounded-lg shadow-sm p-3 sm:p-4 lg:p-6 border border-gray-200">
          <div class="flex items-center just  ify-between">
            <div class="min-w-0 flex-1">
              <p class="text-xs sm:text-sm font-medium text-gray-600 truncate">Total ROI Runs</p>
              <p class="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{{ summaryStats.totalRoiRuns }}</p>
            </div>
            <div class="p-2 sm:p-3 bg-blue-100 rounded-full ml-3">
              <i class="pi pi-calendar text-blue-600 text-sm sm:text-lg lg:text-xl"></i>
            </div>
          </div>
        </div>

        <!-- Total Users Credited -->
        <div class="bg-white rounded-lg shadow-sm p-3 sm:p-4 lg:p-6 border border-gray-200">
          <div class="flex items-center justify-between">
            <div class="min-w-0 flex-1">
              <p class="text-xs sm:text-sm font-medium text-gray-600 truncate">Total Users Credited</p>
              <p class="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{{ summaryStats.totalUsersCredited }}</p>
            </div>
            <div class="p-2 sm:p-3 bg-green-100 rounded-full ml-3">
              <i class="pi pi-users text-green-600 text-sm sm:text-lg lg:text-xl"></i>
            </div>
          </div>
        </div>

        <!-- Total Amount Credited -->
        <div class="bg-white rounded-lg shadow-sm p-3 sm:p-4 lg:p-6 border border-gray-200">
          <div class="flex items-center justify-between">
            <div class="min-w-0 flex-1">
              <p class="text-xs sm:text-sm font-medium text-gray-600 truncate">Total Amount Credited</p>
              <p class="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{{ formatCurrency(summaryStats.totalAmountCredited) }}</p>
            </div>
            <div class="p-2 sm:p-3 bg-purple-100 rounded-full ml-3">
              <img src="/assets/images/icons/UAE_Dirham_Symbol.svg" alt="UAE Dirham" class="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5">
            </div>
          </div>
        </div>

        <!-- Active Portfolios -->
        <div class="bg-white rounded-lg shadow-sm p-3 sm:p-4 lg:p-6 border border-gray-200">
          <div class="flex items-center justify-between">
            <div class="min-w-0 flex-1">
              <p class="text-xs sm:text-sm font-medium text-gray-600 truncate">Active Portfolios</p>
              <p class="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{{ summaryStats.totalPortfoliosActive }}</p>
            </div>
            <div class="p-2 sm:p-3 bg-orange-100 rounded-full ml-3">
              <i class="pi pi-chart-line text-orange-600 text-sm sm:text-lg lg:text-xl"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- ROI Runs History -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200">
        <div class="p-3 sm:p-4 lg:p-6 border-b border-gray-200">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <h2 class="text-lg sm:text-xl font-semibold text-gray-900">ROI Runs History</h2>
            <p class="text-xs sm:text-sm text-gray-600">
              Last run: {{ summaryStats.lastRunDate ? formatDate(summaryStats.lastRunDate) : 'Never' }}
            </p>
          </div>
        </div>
      <div class="p-3 sm:p-4 lg:p-6">
        <p-table
          [value]="roiRuns"
          [loading]="loading.roiRuns"
          [paginator]="true"
          [rows]="5"
          [showCurrentPageReport]="true"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          [rowsPerPageOptions]="[5,10,25,50]"
          [responsive]="true"
          styleClass="p-datatable-sm">
          <ng-template pTemplate="header">
            <tr class="bg-gray-50">
              <th class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Month</th>
              <th class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Run Date</th>
              <th class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Portfolios</th>
              <th class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Users</th>
              <th class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Credited</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-roiRun>
            <tr class="hover:bg-gray-50 transition-colors">
              <td class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3">
                <div class="font-medium text-gray-900 text-xs sm:text-sm">{{ roiRun.monthKey }}</div>
                <div class="sm:hidden text-xs text-gray-500 mt-1">{{ formatDate(roiRun.runAt) }}</div>
              </td>
              <td class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 hidden sm:table-cell">
                <span class="text-gray-700 text-xs sm:text-sm">{{ formatDate(roiRun.runAt) }}</span>
              </td>
              <td class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 hidden md:table-cell">
                <span class="text-gray-700 text-xs sm:text-sm">{{ roiRun.portfoliosProcessed }}</span>
              </td>
              <td class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 hidden lg:table-cell">
                <span class="text-gray-700 text-xs sm:text-sm">{{ roiRun.usersCredited }}</span>
              </td>
              <td class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3">
                <span class="font-semibold text-gray-900 text-xs sm:text-sm">{{ formatCurrency(roiRun.totalCredited) }}</span>
                <div class="md:hidden text-xs text-gray-500 mt-1">
                  <span>{{ roiRun.portfoliosProcessed }} portfolios</span>
                  <span class="ml-2">{{ roiRun.usersCredited }} users</span>
                </div>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="5" class="text-center py-6 sm:py-8 text-gray-500 text-sm sm:text-base">
                No ROI runs found
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    </div>

    <!-- Portfolio ROI Statistics -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200">
      <div class="p-3 sm:p-4 lg:p-6 border-b border-gray-200">
        <h2 class="text-lg sm:text-xl font-semibold text-gray-900">Portfolio ROI Statistics</h2>
      </div>
      <div class="p-3 sm:p-4 lg:p-6">
        <p-table
          [value]="portfolioStats"
          [loading]="loading.portfolioStats"
          [paginator]="true"
          [rows]="10"
          [showCurrentPageReport]="true"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          [responsive]="true"
          styleClass="p-datatable-sm">
          <ng-template pTemplate="header">
            <tr class="bg-gray-50">
              <th class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Portfolio</th>
              <th class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Risk Level</th>
              <th class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">ROI Rate</th>
              <th class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Users</th>
              <th class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Credited</th>
              <th class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Last Credit</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-stat>
            <tr class="hover:bg-gray-50 transition-colors">
              <td class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3">
                <div class="font-medium text-gray-900 text-xs sm:text-sm">{{ stat.portfolioName }}</div>
                <div class="sm:hidden text-xs text-gray-500 mt-1">
                  <span [class]="getRiskLevelClass(stat.riskLevel)" class="px-2 py-1 rounded-full text-xs font-medium mr-2">
                    {{ stat.riskLevel }}
                  </span>
                  <span class="font-semibold">{{ stat.actualRoiPercentage }}%</span>
                </div>
              </td>
              <td class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 hidden sm:table-cell">
                <span [class]="getRiskLevelClass(stat.riskLevel)" class="px-2 py-1 rounded-full text-xs font-medium">
                  {{ stat.riskLevel }}
                </span>
              </td>
              <td class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 hidden md:table-cell">
                <span class="font-semibold text-gray-900 text-xs sm:text-sm">{{ stat.actualRoiPercentage }}%</span>
              </td>
              <td class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 hidden lg:table-cell">
                <span class="text-gray-700 text-xs sm:text-sm">{{ stat.totalUsers }}</span>
              </td>
              <td class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3">
                <span class="font-semibold text-gray-900 text-xs sm:text-sm">{{ formatCurrency(stat.totalCredited) }}</span>
                <div class="lg:hidden text-xs text-gray-500 mt-1">
                  <span>{{ stat.totalUsers }} users</span>
                  <span class="ml-2">{{ formatDate(stat.lastCreditDate) }}</span>
                </div>
              </td>
              <td class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 hidden sm:table-cell">
                <span class="text-gray-600 text-xs sm:text-sm">{{ formatDate(stat.lastCreditDate) }}</span>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="6" class="text-center py-6 sm:py-8 text-gray-500 text-sm sm:text-base">
                No portfolio statistics found
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    </div>

    <!-- ROI Credits Table -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200">
      <div class="p-3 sm:p-4 lg:p-6 border-b border-gray-200">
        <div class="flex flex-col gap-3">
          <h2 class="text-lg sm:text-xl font-semibold text-gray-900">ROI Credits</h2>

          <!-- Filters and Search in single row on larger screens -->
          <div class="flex flex-col lg:flex-row gap-3 sm:gap-4">
            <!-- Left side: Filters -->
            <div class="flex flex-col sm:flex-row gap-3 sm:gap-4 lg:w-2/3">
              <!-- Month Filter -->
              <div class="flex items-center gap-2 min-w-0">
                <label class="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap">Month:</label>
                <p-dropdown
                  [options]="getMonths()"
                  [(ngModel)]="selectedMonth"
                  placeholder="All Months"
                  (onChange)="onMonthFilterChange()"
                  styleClass="w-full sm:w-32"
                  [showClear]="true">
                </p-dropdown>
              </div>
              <!-- Portfolio Filter -->
              <div class="flex items-center gap-2 min-w-0">
                <label class="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap">Portfolio:</label>
                <p-dropdown
                  [options]="getUniquePortfolios()"
                  [(ngModel)]="selectedPortfolio"
                  optionLabel="name"
                  optionValue="id"
                  placeholder="All Portfolios"
                  (onChange)="onPortfolioFilterChange()"
                  styleClass="w-full sm:w-40 lg:w-48"
                  [showClear]="true">
                </p-dropdown>
              </div>
            </div>

            <!-- Right side: Search (grows to fill remaining space) -->
            <div class="flex items-center gap-2 lg:w-1/3">
              <label class="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap">Search:</label>
              <div class="relative flex-1">
                <input
                  pInputText
                  [(ngModel)]="searchUser"
                  placeholder="Name or email..."
                  (input)="onSearchChange()"
                  class="w-full pr-10">
                <button *ngIf="searchUser" (click)="searchUser=''; onSearchChange()" class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <i-lucide name="x" class="h-4 w-4"></i-lucide>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="p-3 sm:p-4 lg:p-6">
        <p-table
          [value]="roiCredits"
          [loading]="loading.roiCredits"
          [paginator]="true"
          [rows]="pageSize"
          [rowsPerPageOptions]="[5,10,25,50]"
          [showCurrentPageReport]="true"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          (onPageChange)="onPageChange($event)"
          [responsive]="true"
          styleClass="p-datatable-sm">
          <ng-template pTemplate="header">
            <tr class="bg-gray-50">
              <th class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
              <th class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Portfolio</th>
              <th class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Risk</th>
              <th class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Amount</th>
              <th class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Consumed</th>
              <th class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Available</th>
              <th class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Date</th>
              <th class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Month</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-credit>
            <tr class="hover:bg-gray-50 transition-colors">
              <td class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3">
                <div>
                  <div class="font-medium text-gray-900 text-xs sm:text-sm">
                    <a (click)="viewUserDetails(credit.userId)" class="cursor-pointer underline text-blue-600 hover:text-blue-800">
                      {{ credit.userName }}
                    </a>
                  </div>
                  <div class="text-xs text-gray-500">{{ credit.userEmail }}</div>
                  <div class="sm:hidden text-xs text-gray-500 mt-1">
                    <span class="font-medium">{{ credit.portfolioName }}</span>
                    <span class="ml-2">{{ formatCurrency(credit.amount) }}</span>
                  </div>
                </div>
              </td>
              <td class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 hidden sm:table-cell">
                <span class="font-medium text-gray-900 text-xs sm:text-sm">{{ credit.portfolioName }}</span>
              </td>
              <td class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 hidden md:table-cell">
                <span [class]="getRiskLevelClass(credit.portfolioRiskLevel)" class="px-2 py-1 rounded-full text-xs font-medium">
                  {{ credit.portfolioRiskLevel }}
                </span>
              </td>
              <td class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 hidden lg:table-cell">
                <span class="font-semibold text-gray-900 text-xs sm:text-sm">{{ formatCurrency(credit.amount) }}</span>
              </td>
              <td class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 hidden lg:table-cell">
                <span class="text-gray-700 text-xs sm:text-sm">{{ formatCurrency(credit.consumedAmount) }}</span>
              </td>
              <td class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3">
                <span class="font-semibold text-green-600 text-xs sm:text-sm">{{ formatCurrency(credit.amount - credit.consumedAmount) }}</span>
                <div class="lg:hidden text-xs text-gray-500 mt-1">
                  <span>{{ formatDate(credit.creditDate) }}</span>
                  <span class="ml-2">{{ credit.monthKey }}</span>
                </div>
              </td>
              <td class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 hidden sm:table-cell">
                <span class="text-gray-600 text-xs sm:text-sm">{{ formatDate(credit.creditDate) }}</span>
              </td>
              <td class="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 hidden md:table-cell">
                <span class="text-gray-600 text-xs sm:text-sm">{{ credit.monthKey }}</span>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="8" class="text-center py-6 sm:py-8 text-gray-500 text-sm sm:text-base">
                No ROI credits found
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    </div>
  </div>
</div>

<!-- User Details Dialog -->
<p-dialog
  [(visible)]="showUserDetailsDialog"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{width: '95vw', 'max-width': '800px'}"
  [contentStyle]="{'max-height': '85vh', 'overflow': 'auto'}"
  header="User Details">
  <app-view-user-details [userId]="selectedUserId"></app-view-user-details>
  <ng-template pTemplate="footer">
    <div class="flex justify-end gap-2">
      <p-button
        icon="pi pi-times"
        label="Close"
        (onClick)="showUserDetailsDialog = false"
        styleClass="p-button-text">
      </p-button>
    </div>
  </ng-template>
</p-dialog>
